import type { Unit } from './lexer'

/**
 * What a script says, as a tree. Comments ride on the node they stand above so an edit made
 * through the editor keeps them — the script is the artifact, and somebody's notes in it are
 * part of what they wrote.
 */

/** Where a node came from, so anything that goes wrong later can point at it. */
export interface Where {
  readonly line: number
  readonly column: number
}

export const NOWHERE: Where = { line: 0, column: 0 }

export type Expr =
  | { readonly type: 'number'; readonly value: number; readonly unit: Unit; readonly at: Where }
  | { readonly type: 'text'; readonly value: string; readonly at: Where }
  | { readonly type: 'name'; readonly name: string; readonly at: Where }
  /** A `$name` in a preset's shape. It matches anything, and remembers what it matched. */
  | { readonly type: 'hole'; readonly name: string; readonly at: Where }
  | {
      readonly type: 'member'
      readonly target: Expr
      readonly name: string
      readonly optional: boolean
      readonly at: Where
    }
  | { readonly type: 'index'; readonly target: Expr; readonly index: Expr; readonly at: Where }
  | {
      readonly type: 'call'
      readonly callee: Expr
      readonly args: readonly Expr[]
      /** Named arguments, in the order they were written — `auto(bar: 20kg, jump: 10kg)`. */
      readonly named: readonly (readonly [string, Expr])[]
      readonly at: Where
    }
  | { readonly type: 'unary'; readonly op: string; readonly operand: Expr; readonly at: Where }
  | {
      readonly type: 'binary'
      readonly op: string
      readonly left: Expr
      readonly right: Expr
      readonly at: Where
    }
  | {
      readonly type: 'ternary'
      readonly condition: Expr
      readonly then: Expr
      readonly otherwise: Expr
      readonly at: Where
    }
  | {
      readonly type: 'arrow'
      readonly params: readonly string[]
      readonly body: Expr
      readonly at: Where
    }
  | { readonly type: 'array'; readonly items: readonly Expr[]; readonly at: Where }

/** A property written in parentheses: `(snap: 2.5kg, rest: 3m)`. */
export interface Prop {
  readonly name: string
  readonly value: Expr
  readonly at: Where
}

export type Stmt =
  | {
      readonly type: 'declare'
      readonly mutable: boolean
      readonly name: string
      readonly value: Expr
      readonly at: Where
    }
  | {
      readonly type: 'assign'
      readonly target: Expr
      readonly op: string
      readonly value: Expr
      readonly at: Where
    }
  | {
      readonly type: 'if'
      readonly condition: Expr
      readonly then: readonly Stmt[]
      readonly otherwise: readonly Stmt[]
      readonly at: Where
    }
  | {
      readonly type: 'for'
      readonly init: Stmt | null
      readonly condition: Expr | null
      readonly step: Stmt | null
      readonly body: readonly Stmt[]
      readonly at: Where
    }
  | {
      readonly type: 'forOf'
      readonly name: string
      readonly source: Expr
      readonly body: readonly Stmt[]
      readonly at: Where
    }
  | { readonly type: 'yield'; readonly line: SetLine; readonly at: Where }
  | { readonly type: 'return'; readonly line: SetLine | null; readonly at: Where }
  | { readonly type: 'expr'; readonly expr: Expr; readonly at: Where }

/** What surrounds a set: the rest before it, whether it is filmed, anything written on it. */
export interface Mods {
  readonly rest?: Expr | undefined
  readonly video?: boolean | undefined
  readonly notes?: string | undefined
}

/** `[warmup] [count x] primary [@ weight] [rest t] [video] ["notes"]` */
export interface SetLine {
  readonly type: 'set'
  readonly warmup: boolean
  readonly count: Expr | null
  readonly primary: Expr
  readonly weight: Expr | null
  readonly mods: Mods
  readonly comments: readonly string[]
  readonly at: Where
}

/**
 * `[warmup] auto(bar: 20kg, jump: 20kg, to: 90%) [rest t] [video] ["notes"]` — a ramp of sets
 * climbing to the work, wearing the same modifiers any other line wears.
 */
export interface AutoLine {
  readonly type: 'auto'
  readonly warmup: boolean
  readonly args: readonly (readonly [string, Expr])[]
  readonly mods: Mods
  readonly comments: readonly string[]
  readonly at: Where
}

/**
 * `dynamic "what it does" { ... }` — a block carries nothing of its own. What surrounds the
 * sets it stands for is written on the set lines it yields, which is where anyone reading it
 * would look, and leaves one place for each thing to be said.
 */
export interface DynamicLine {
  readonly type: 'dynamic'
  readonly label: string
  readonly body: readonly Stmt[]
  readonly comments: readonly string[]
  readonly at: Where
}

export type Line = SetLine | AutoLine | DynamicLine

export interface ExerciseNode {
  readonly name: string
  readonly props: readonly Prop[]
  readonly lines: readonly Line[]
  readonly comments: readonly string[]
  readonly at: Where
}

export type GroupKind = 'superset' | 'choose' | 'rotate'

export const GROUP_KINDS: readonly GroupKind[] = ['superset', 'choose', 'rotate']

export interface ExerciseItem {
  readonly type: 'exercise'
  readonly exercise: ExerciseNode
}

export interface GroupItem {
  readonly type: 'group'
  readonly kind: GroupKind
  readonly exercises: readonly ExerciseNode[]
  /** Only a superset takes any: `rest`, taken before every round after the first. */
  readonly props: readonly Prop[]
  readonly comments: readonly string[]
  readonly at: Where
}

export type WorkoutItem = ExerciseItem | GroupItem

export interface WorkoutNode {
  readonly name: string
  readonly props: readonly Prop[]
  readonly items: readonly WorkoutItem[]
  readonly comments: readonly string[]
  readonly at: Where
}

export type Schedule = 'free' | 'sequential' | 'strict'

export const SCHEDULES: readonly Schedule[] = ['free', 'sequential', 'strict']

export interface TemplateNode {
  readonly name: string
  readonly props: readonly Prop[]
  readonly workouts: readonly WorkoutNode[]
  readonly version: number
  readonly comments: readonly string[]
  readonly at: Where
}

/** The version of the language a file is written against. */
export const LANGUAGE_VERSION = 1

/** Every exercise a workout holds, groups flattened. */
export function workoutExercises(workout: WorkoutNode): ExerciseNode[] {
  return workout.items.flatMap((item) =>
    item.type === 'exercise' ? [item.exercise] : [...item.exercises],
  )
}

export function templateSchedule(template: TemplateNode): Schedule {
  const value = template.props.find((prop) => prop.name === 'schedule')?.value
  if (value?.type === 'name' && (SCHEDULES as readonly string[]).includes(value.name)) {
    return value.name as Schedule
  }
  return 'free'
}

/** What the template says it is, in the author's own words. Null when it says nothing. */
export function templateDescription(template: TemplateNode): string | null {
  const found = textProp(template.props, 'description')
  return found === null || found.trim().length === 0 ? null : found
}

export function templateCycle(template: TemplateNode): number | null {
  const value = template.props.find((prop) => prop.name === 'cycle')?.value
  return value?.type === 'number' ? Math.trunc(value.value) : null
}

/** A number property, in the unit everything is held in. */
export function numberProp(props: readonly Prop[], name: string): number | null {
  const value = props.find((prop) => prop.name === name)?.value
  return value?.type === 'number' ? value.value * value.unit.factor : null
}

export function textProp(props: readonly Prop[], name: string): string | null {
  const value = props.find((prop) => prop.name === name)?.value
  return value?.type === 'text' ? value.value : null
}

/**
 * The set lines a block stands for — everything it yields or returns, however deeply nested
 * in the loops and branches that decide them. This is where a block says what surrounds its
 * sets, since the block itself carries nothing.
 */
export function blockSets(line: DynamicLine): SetLine[] {
  const found: SetLine[] = []
  const walk = (body: readonly Stmt[]): void => {
    for (const stmt of body) {
      switch (stmt.type) {
        case 'yield':
          found.push(stmt.line)
          break
        case 'return':
          if (stmt.line !== null) {
            found.push(stmt.line)
          }
          break
        case 'if':
          walk(stmt.then)
          walk(stmt.otherwise)
          break
        case 'for':
        case 'forOf':
          walk(stmt.body)
          break
        default:
          break
      }
    }
  }
  walk(line.body)
  return found
}

/** The same block with every set line it stands for put through a change. */
export function mapBlockSets(line: DynamicLine, change: (set: SetLine) => SetLine): DynamicLine {
  const walk = (body: readonly Stmt[]): Stmt[] =>
    body.map((stmt): Stmt => {
      switch (stmt.type) {
        case 'yield':
          return { ...stmt, line: change(stmt.line) }
        case 'return':
          return stmt.line === null ? stmt : { ...stmt, line: change(stmt.line) }
        case 'if':
          return { ...stmt, then: walk(stmt.then), otherwise: walk(stmt.otherwise) }
        case 'for':
        case 'forOf':
          return { ...stmt, body: walk(stmt.body) }
        default:
          return stmt
      }
    })
  return { ...line, body: walk(line.body) }
}

export function flagProp(props: readonly Prop[], name: string): boolean {
  const value = props.find((prop) => prop.name === name)?.value
  return value?.type === 'name' && value.name === 'true'
}

/**
 * Two names are the same exercise when they match ignoring case, spacing and punctuation.
 *
 * This is the one definition. A set stores what this returns as its `exercise_key`, so the
 * fold *is* an exercise's identity — the server, both clients and this package have to agree
 * on it exactly or a name means one exercise on one side and another on the other. Letters and
 * decimal digits are kept, which is precisely what Kotlin's `Char.isLetterOrDigit()` keeps, so
 * the port in `apps/android/.../lang/Ast.kt` stays character-for-character equivalent.
 *
 * Not `\p{N}`: that also admits Nl and No (`Ⅷ`, `½`), which `isLetterOrDigit()` drops.
 */
export function normalizeExerciseName(name: string): string {
  return name.toLowerCase().replace(/[^\p{L}\p{Nd}]/gu, '')
}
